import {
  getSpeculativeLoadingBudget,
  mediaQueryLarge,
  prefersReducedMotion,
  requestIdleCallback,
  startViewTransition,
} from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';
import { StandardEvents } from '@shopify/events';
import { getIntersectionRoot, getScrollTop, scrollTo, syncScrollRestorationMode } from '@theme/scroll-container';

const COLLECTION_VIEW_STORAGE_KEY = 'tfp-collection-view-v1';
const COLLECTION_VIEWS = new Set(['standard', 'lookbook', 'single']);
const SEARCH_VIEWS = new Set(['standard', 'lookbook']);
const EDITORIAL_VIEW_STORAGE_KEY = 'tfp-editorial-view-v1';
const EDITORIAL_VIEWS = new Set(['editorial', 'quick-buy']);
const ADAPTIVE_DESKTOP_QUERY = window.matchMedia('(min-width: 990px)');
const MAX_ADAPTIVE_APPENDS = 8;
const MAX_MOBILE_COLLECTION_APPENDS = 2;
const COLLECTION_MEDIA_COALESCE_MS = 140;
const HISTORY_RESTORE_TIMEOUT = 7000;
const HISTORY_POSITION_SETTLE_MS = 500;
const HISTORY_POSITION_SETTLE_MAX_FRAMES = 30;
const HISTORY_POSITION_SETTLE_STABLE_FRAMES = 3;
const HISTORY_POSITION_EPSILON = 0.5;
const LOAD_MORE_FEEDBACK_FRAME_TIMEOUT = 120;

/** Gives the busy state a bounded, visible frame before cached page work starts. */
function waitForLoadMoreFeedbackFrame() {
  if (document.hidden) return Promise.resolve();

  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      resolve();
    };
    const timeout = window.setTimeout(finish, LOAD_MORE_FEEDBACK_FRAME_TIMEOUT);
    requestAnimationFrame(() => window.setTimeout(finish, 0));
  });
}

let editorialQuickBuyRuntime;

function loadEditorialQuickBuyRuntime() {
  editorialQuickBuyRuntime ||= import('@theme/product-form').catch((error) => {
    editorialQuickBuyRuntime = undefined;
    console.warn('[results-list] Quick Buy runtime unavailable:', error);
  });
  return editorialQuickBuyRuntime;
}

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  // Only decoded, already-pending collection media enters these bounded cohorts.
  // Network scheduling and the initial eager/LCP surface remain ProductCard owners.
  #mediaRevealCohorts = new Map();

  queueCardMediaReveal(card) {
    if (!this.isConnected || this.dataset.catalogViewContract !== 'standard-lookbook' ||
        !this.closest('collection-component') || !card.isMediaRevealReady()) return false;
    const grid = this.refs.grid;
    const item = card.closest('.product-grid__item');
    if (!grid || item?.parentElement !== grid) return false;
    const bounds = item.getBoundingClientRect();
    // Finish lookahead work before the shopper reaches it; never animate a
    // previously visible card again just to manufacture a synchronized entrance.
    if (bounds.top >= window.innerHeight || bounds.bottom <= 0 || prefersReducedMotion()) return false;

    const top = item.offsetTop;
    const sameRow = (peer) => peer instanceof HTMLElement &&
      peer.classList.contains('product-grid__item') && Math.abs(peer.offsetTop - top) <= 1;
    let anchor = item;
    while (sameRow(anchor.previousElementSibling)) anchor = anchor.previousElementSibling;
    const row = [];
    for (let peer = anchor; sameRow(peer); peer = peer.nextElementSibling) row.push(peer);
    let cohort = this.#mediaRevealCohorts.get(anchor);
    if (!cohort) {
      cohort = { cards: new Set(), timer: undefined, frame: undefined };
      this.#mediaRevealCohorts.set(anchor, cohort);
      // This deadline starts with the first ready member and is never extended.
      cohort.timer = window.setTimeout(() => this.#flushMediaRevealCohort(anchor), COLLECTION_MEDIA_COALESCE_MS);
    }
    cohort.cards.add(card);
    const pending = row.flatMap((peer) => Array.from(peer.querySelectorAll('product-card'))).filter((peer) =>
      peer.querySelector('[data-media-pending]')
    );
    if (pending.every((peer) => peer.isMediaRevealReady?.())) {
      for (const peer of pending) cohort.cards.add(peer);
      if (cohort.frame === undefined) {
        cohort.frame = requestAnimationFrame(() => this.#flushMediaRevealCohort(anchor));
      }
    }
    return true;
  }

  cancelCardMediaReveal(card) {
    for (const [anchor, cohort] of this.#mediaRevealCohorts) {
      cohort.cards.delete(card);
      if (cohort.cards.size) continue;
      window.clearTimeout(cohort.timer);
      if (cohort.frame !== undefined) cancelAnimationFrame(cohort.frame);
      this.#mediaRevealCohorts.delete(anchor);
    }
  }

  #flushMediaRevealCohort(anchor) {
    const cohort = this.#mediaRevealCohorts.get(anchor);
    if (!cohort) return;
    this.#mediaRevealCohorts.delete(anchor);
    window.clearTimeout(cohort.timer);
    if (cohort.frame !== undefined) cancelAnimationFrame(cohort.frame);
    for (const card of cohort.cards) {
      if (card.isConnected && card.closest('results-list') === this) card.revealReadyMedia();
    }
  }

  #flushAllMediaRevealCohorts() {
    for (const anchor of this.#mediaRevealCohorts.keys()) this.#flushMediaRevealCohort(anchor);
  }

  #successfulAdaptiveAppends = 0;

  #renderedCollectionSignature = '';

  #collectionPopRenderPending = false;

  #collectionPopRenderGeneration = 0;

  #searchPopRenderGeneration = 0;

  #adaptiveHistoryRestoreGeneration = 0;

  #adaptiveHistoryRestorePending = false;

  #adaptiveHistoryRestoreKey = '';

  #adaptiveHistoryRestoreTask;

  #historyRestoreTimer;

  #historyRestoreIntent = 0;

  // The small pre-upgrade gate in main-collection hands its fail-open timer to this owner.
  historyGateTimer;

  #collectionPageShowFrame;

  #persistedCollectionUrl = '';

  #collectionScrollIntent = 0;

  #departureAnchorId = '';

  #positionRestoreFrame;

  #positionRestoreResolve;

  #positionRestoreGeneration = 0;

  #handleCollectionScrollIntent = (event) => {
    if (event.type === 'keydown' && !['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(event.key)) return;
    this.#collectionScrollIntent += 1;
    this.#departureAnchorId = '';
    this.#cancelCollectionPositionRestore();
    this.removeAttribute('data-history-positioning');
  };

  #handleCollectionPageHide = () => {
    this.captureCollectionHistory();
    this.#cancelCollectionPositionRestore();
  };

  /** @type {IntersectionObserver | undefined} */
  #backToTopObserver;

  /** @type {IntersectionObserver | undefined} */
  #loadMorePrefetchObserver;

  #loadMoreFailureFallback = false;

  /** @type {IntersectionObserver | undefined} */
  #editorialMediaObserver;

  /** @type {MutationObserver | undefined} */
  #editorialGridObserver;

  /** @type {HTMLElement | undefined} */
  #editorialObservedGrid;

  get paginationMode() {
    const mobileMode = this.dataset.mobilePaginationMode;
    const configuredMode = !ADAPTIVE_DESKTOP_QUERY.matches && (mobileMode === 'adaptive' || mobileMode === 'pagination')
      ? mobileMode : super.paginationMode;
    if (configuredMode === 'pagination') return configuredMode;
    if (this.dataset.catalogViewContract === 'standard-lookbook' && this.hasAttribute('data-adaptive-pagination-locked')) {
      return 'adaptive';
    }
    return configuredMode;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    ADAPTIVE_DESKTOP_QUERY.addEventListener('change', this.#handleMediaQueryChange);
    if (this.#isCollectionCatalog()) {
      this.#renderedCollectionSignature = this.#collectionSignature();
      this.#setCollectionNavigationState('ready');

      if (this.#isSearchResults()) {
        window.addEventListener('popstate', this.#handleSearchPopState);
      } else {
        document.addEventListener(StandardEvents.collectionUpdate, this.#handleCollectionUpdate);
        window.addEventListener('popstate', this.#handleCollectionPopState);
        window.addEventListener('pageshow', this.#handleCollectionPageShow);
        window.addEventListener('pagehide', this.#handleCollectionPageHide);
        window.addEventListener('pointerdown', this.#handleCollectionScrollIntent, { passive: true });
        window.addEventListener('wheel', this.#handleCollectionScrollIntent, { passive: true });
        window.addEventListener('touchmove', this.#handleCollectionScrollIntent, { passive: true });
        window.addEventListener('keydown', this.#handleCollectionScrollIntent);
        const state = typeof history.state === 'object' && history.state !== null ? history.state : {};
        if (!state.tfpCollectionHistory || !state.tfpCollectionEntryId) {
          history.replaceState({ ...state, tfpCollectionHistory: true,
            tfpCollectionEntryId: state.tfpCollectionEntryId || window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}` }, '');
        }
        // Start during connection: pageshow may have fired before this deferred module loaded.
        if (this.hasAttribute('data-history-restoring')) {
          this.#restoreCollectionView();
          void this.#restoreAdaptiveHistory(history.state ?? {});
        }
      }

      const initialRestore = !this.hasAttribute('initialized');
      requestAnimationFrame(() => {
        if (this.isConnected) this.#restoreCollectionView(initialRestore);
      });
    }
    this.setAttribute('initialized', '');
  }

  updatedCallback() {
    super.updatedCallback();
    this.#syncCollectionDocumentTitle();
    if (this.#collectionPopRenderPending && history.state?.tfpCollectionPage > history.state?.tfpCollectionStartPage) {
      this.setAttribute('data-history-restoring', '');
    }
    this.#observeLoadMorePrefetch();
  }

  disconnectedCallback() {
    this.#loadMorePrefetchObserver?.disconnect();
    this.#loadMorePrefetchObserver = undefined;
    this.#flushAllMediaRevealCohorts();
    this.#collectionPopRenderGeneration += 1;
    this.#searchPopRenderGeneration += 1;
    this.#collectionPopRenderPending = false;
    this.#clearHistoryGate();
    window.clearTimeout(this.#historyRestoreTimer);
    cancelAnimationFrame(this.#collectionPageShowFrame);
    this.#cancelCollectionPositionRestore();
    this.#departureAnchorId = '';
    this.#persistedCollectionUrl = '';
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    ADAPTIVE_DESKTOP_QUERY.removeEventListener('change', this.#handleMediaQueryChange);
    if (this.#isCollectionCatalog()) {
      if (this.#isSearchResults()) {
        window.removeEventListener('popstate', this.#handleSearchPopState);
      } else {
        document.removeEventListener(StandardEvents.collectionUpdate, this.#handleCollectionUpdate);
        window.removeEventListener('popstate', this.#handleCollectionPopState);
        window.removeEventListener('pageshow', this.#handleCollectionPageShow);
        window.removeEventListener('pagehide', this.#handleCollectionPageHide);
        window.removeEventListener('pointerdown', this.#handleCollectionScrollIntent);
        window.removeEventListener('wheel', this.#handleCollectionScrollIntent);
        window.removeEventListener('touchmove', this.#handleCollectionScrollIntent);
        window.removeEventListener('keydown', this.#handleCollectionScrollIntent);
      }
      this.#cancelBackToTop();
      this.#editorialMediaObserver?.disconnect();
      this.#editorialMediaObserver = undefined;
      this.#editorialGridObserver?.disconnect();
      this.#editorialGridObserver = undefined;
      this.#editorialObservedGrid = undefined;
      this.#adaptiveHistoryRestoreGeneration += 1;
      this.#adaptiveHistoryRestorePending = false;
    }
    super.disconnectedCallback();
  }

  #adaptiveAppendLimit() {
    return this.dataset.catalogViewContract === 'standard-lookbook' && this.closest('collection-component')
      ? MAX_MOBILE_COLLECTION_APPENDS
      : MAX_ADAPTIVE_APPENDS;
  }

  #syncLoadMorePrevious() {
    const { grid, loadMorePrevious } = this.refs;
    if (!(loadMorePrevious instanceof HTMLAnchorElement) || this.dataset.catalogViewContract !== 'standard-lookbook') return;
    const firstCard = grid?.querySelector(':scope > .product-grid__item[data-page]');
    const firstPage = Number(firstCard?.getAttribute('data-page'));
    if (!Number.isInteger(firstPage) || firstPage < 1) return;
    loadMorePrevious.hidden = firstPage === 1;
    if (firstPage === 1) {
      loadMorePrevious.removeAttribute('href');
      return;
    }
    const previousUrl = new URL(window.location.href);
    previousUrl.hash = '';
    previousUrl.searchParams.set('page', String(firstPage - 1));
    loadMorePrevious.href = previousUrl.toString();
  }

  #syncLoadMoreControl() {
    if (!this.isConnected || !this.closest('collection-component')) return;
    if (this.paginationMode === 'pagination') this.removeAttribute('data-adaptive-pagination-locked');
    this.#syncLoadMorePrevious();
    const { loadMore, loadMoreLabel } = this.refs;
    if (!(loadMore instanceof HTMLAnchorElement || loadMore instanceof HTMLButtonElement)) return;

    const useButton = !this.#loadMoreFailureFallback && this.paginationMode === 'adaptive' &&
      (!ADAPTIVE_DESKTOP_QUERY.matches || this.hasAttribute('data-adaptive-pagination-locked')) &&
      this.#successfulAdaptiveAppends < this.#adaptiveAppendLimit();
    if (!this.#loadMoreFailureFallback && this.getAttribute('aria-busy') !== 'true' &&
        this.dataset.catalogViewContract === 'standard-lookbook' && loadMoreLabel) {
      loadMoreLabel.textContent = (useButton ? this.dataset.loadMoreIdleLabel : this.dataset.loadMoreNextPageLabel)
        ?? this.dataset.loadMoreIdleLabel ?? loadMoreLabel.textContent;
    }
    if (useButton === (loadMore instanceof HTMLButtonElement)) return;
    const nextUrl = loadMore instanceof HTMLAnchorElement ? loadMore.getAttribute('href') : loadMore.dataset.nextUrl;
    if (!nextUrl) return;

    // Keep a real server link as the fallback; enhanced append is an action, so
    // Shopify's document speculation must not also prefetch its next-page URL.
    const control = document.createElement(useButton ? 'button' : 'a');
    for (const { name, value } of loadMore.attributes) {
      if (!['href', 'type', 'data-next-url', 'disabled', 'aria-disabled'].includes(name)) {
        control.setAttribute(name, value);
      }
    }
    if (useButton) {
      control.setAttribute('type', 'button');
      control.setAttribute('data-next-url', nextUrl);
    } else {
      control.setAttribute('href', nextUrl);
    }
    const returnFocus = document.activeElement === loadMore;
    control.append(...loadMore.childNodes);
    loadMore.replaceWith(control);
    super.updatedCallback();
    if (returnFocus) control.focus({ preventScroll: true });
  }

  #canPrefetchLoadMore() {
    return !this.#loadMoreFailureFallback && this.isConnected && this.paginationMode === 'adaptive' &&
      Boolean(this.closest('collection-component')) &&
      (!ADAPTIVE_DESKTOP_QUERY.matches || this.hasAttribute('data-adaptive-pagination-locked')) &&
      this.#successfulAdaptiveAppends < this.#adaptiveAppendLimit() &&
      this.getAttribute('aria-busy') !== 'true' && !this.#adaptiveHistoryRestorePending &&
      this.dataset.collectionNavigationState === 'ready' &&
      getSpeculativeLoadingBudget() !== 'none';
  }

  #observeLoadMorePrefetch() {
    this.#loadMorePrefetchObserver?.disconnect();
    this.#loadMorePrefetchObserver = undefined;
    this.#syncLoadMoreControl();
    if (!this.#canPrefetchLoadMore() || !('IntersectionObserver' in window)) return;
    const { loadMore } = this.refs;
    if (!(loadMore instanceof HTMLButtonElement) || loadMore.hidden || !loadMore.dataset.nextUrl) return;

    const lookahead = getSpeculativeLoadingBudget() === 'balanced' ? 900 : 300;
    this.#loadMorePrefetchObserver = new IntersectionObserver((entries) => {
      if (!this.#canPrefetchLoadMore()) return;
      for (const entry of entries) {
        if (entry.isIntersecting && entry.target === this.refs.loadMore) {
          // HTML only: images and product components stay untouched until the shopper appends.
          void this.prefetchNextPage().catch(() => {});
        }
      }
    }, { root: getIntersectionRoot(), rootMargin: `0px 0px ${lookahead}px 0px` });
    this.#loadMorePrefetchObserver.observe(loadMore);
  }

  #cancelBackToTop() {
    this.#backToTopObserver?.disconnect();
    this.#backToTopObserver = undefined;

    const { backToTop } = this.refs;
    if (backToTop instanceof HTMLButtonElement) {
      backToTop.removeAttribute('data-visible');
      backToTop.setAttribute('aria-hidden', 'true');
      backToTop.tabIndex = -1;
    }
  }

  #armBackToTop() {
    this.#cancelBackToTop();

    const { grid, backToTop } = this.refs;
    if (!(grid instanceof HTMLElement) || !(backToTop instanceof HTMLButtonElement)) return;
    if (!('IntersectionObserver' in window)) return;

    const cards = Array.from(grid.children).filter(
      (child) => child instanceof HTMLElement && child.classList.contains('product-grid__item')
    );
    if (cards.length < 4) return;

    const resolvedColumns = getComputedStyle(grid).gridTemplateColumns;
    const columnCount = resolvedColumns && resolvedColumns !== 'none'
      ? Math.max(1, resolvedColumns.split(/\s+/).filter(Boolean).length)
      : 1;

    // Observe the first card of the fourth visual row. This keeps the trigger
    // perceptually consistent across Standard, Lookbook and Editorial layouts.
    const triggerCard = cards[columnCount * 3];
    if (!(triggerCard instanceof HTMLElement)) return;

    this.#backToTopObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        const rootTop = entry.rootBounds?.top ?? 0;
        const isPastUsefulDepth = !entry.isIntersecting && entry.boundingClientRect.bottom < rootTop;

        backToTop.toggleAttribute('data-visible', isPastUsefulDepth);
        backToTop.setAttribute('aria-hidden', String(!isPastUsefulDepth));
        backToTop.tabIndex = isPastUsefulDepth ? 0 : -1;
      },
      { root: getIntersectionRoot(), threshold: 0 }
    );

    this.#backToTopObserver.observe(triggerCard);
  }

  backToTop() {
    scrollTo({
      top: 0,
      behavior: prefersReducedMotion() ? 'instant' : 'smooth',
    });
  }

  /**
   * Appends products through the enhanced mobile control; full-navigation links stay native.
   * @param {MouseEvent} event
   */
  async loadMore(event) {
    if (
      (ADAPTIVE_DESKTOP_QUERY.matches && !this.hasAttribute('data-adaptive-pagination-locked')) ||
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    const loadMore = event.target instanceof Element ? event.target.closest('[ref="loadMore"]') : null;
    if (!(loadMore instanceof HTMLButtonElement)) return;
    if (this.#successfulAdaptiveAppends >= this.#adaptiveAppendLimit()) return;

    event.preventDefault();
    if (this.getAttribute('aria-busy') === 'true') return;

    const appendGeneration = this.#adaptiveHistoryRestoreGeneration;
    const navigationGeneration = this.#collectionPopRenderGeneration;
    const isCurrentAppend = () =>
      this.isConnected &&
      appendGeneration === this.#adaptiveHistoryRestoreGeneration &&
      navigationGeneration === this.#collectionPopRenderGeneration;

    this.#setLoadMoreBusy(true);
    try {
      await waitForLoadMoreFeedbackFrame();
      if (!isCurrentAppend()) return;
      const result = await this.appendNextPage({ historyMode: 'replace' });
      if (!isCurrentAppend()) return;
      if (!result) throw new Error('No products were appended');
      this.#successfulAdaptiveAppends += 1;
      this.dataset.adaptivePaginationLocked = 'true';
      this.#updateLoadMorePresentation(result.nextUrl, result.addedElements.length);
      this.#restoreCollectionView();
      void this.#saveCollectionSnapshot();
    } catch {
      if (!isCurrentAppend()) return;
      const { loadMoreLabel, loadMoreStatus } = this.refs;
      if (loadMoreLabel) loadMoreLabel.textContent = this.dataset.loadMoreRetryLabel ?? '';
      if (loadMoreStatus) loadMoreStatus.textContent = this.dataset.loadMoreErrorMessage ?? '';
      this.#loadMoreFailureFallback = true;
      this.#syncLoadMoreControl();
    } finally {
      if (isCurrentAppend()) this.#setLoadMoreBusy(false);
    }
  }

  #setLoadMoreBusy(isBusy) {
    const { grid, loadMore, loadMoreLabel } = this.refs;
    if (isBusy) {
      this.setAttribute('aria-busy', 'true');
      grid?.setAttribute('aria-busy', 'true');
    } else {
      this.removeAttribute('aria-busy');
      grid?.removeAttribute('aria-busy');
    }
    if (loadMore instanceof HTMLButtonElement) {
      loadMore.disabled = isBusy;
      loadMore.setAttribute('aria-disabled', String(isBusy));
    } else if (loadMore) {
      loadMore.removeAttribute('aria-disabled');
    }
    if (isBusy && loadMoreLabel) loadMoreLabel.textContent = this.dataset.loadMoreLoadingLabel ?? '';
    if (!isBusy) this.#observeLoadMorePrefetch();
  }

  #updateLoadMorePresentation(nextUrl, addedCount) {
    const { grid, loadMoreStatus, loadMoreControl } = this.refs;
    if (!(grid instanceof HTMLElement) || !(loadMoreControl instanceof HTMLElement)) return;

    const previousLoaded = Number(loadMoreControl.dataset.loadedCount) || 0;
    const total = Number(loadMoreControl.dataset.totalCount) || previousLoaded + addedCount;
    const loaded = Math.min(total, previousLoaded + addedCount);

    if (loadMoreStatus) {
      loadMoreStatus.textContent = this.#formatTemplate(this.dataset.loadMoreLoadedTemplate ?? '', {
        count: addedCount,
        loaded,
        total,
      });
    }

    this.#applyLoadMorePresentation({ nextUrl, loaded, total });
  }

  #applyLoadMorePresentation({ nextUrl, loaded, total }) {
    const { loadMore, loadMoreLabel, loadMoreControl, loadMoreComplete } = this.refs;
    if (!(loadMoreControl instanceof HTMLElement)) return;

    loadMoreControl.dataset.loadedCount = String(loaded);
    this.#updateLoadMoreProgress(loaded, total);
    if (loadMoreLabel) loadMoreLabel.textContent = this.dataset.loadMoreIdleLabel ?? '';
    if (loadMore instanceof HTMLAnchorElement || loadMore instanceof HTMLButtonElement) {
      if (nextUrl) {
        if (loadMore instanceof HTMLAnchorElement) loadMore.href = nextUrl;
        else loadMore.dataset.nextUrl = nextUrl;
        loadMore.hidden = false;
        if (loadMoreComplete instanceof HTMLElement) loadMoreComplete.hidden = true;
      } else {
        const returnFocus = document.activeElement === loadMore;
        loadMore.removeAttribute('href');
        delete loadMore.dataset.nextUrl;
        loadMore.hidden = true;
        if (loadMoreComplete instanceof HTMLElement) {
          loadMoreComplete.hidden = false;
          if (returnFocus) loadMoreComplete.focus({ preventScroll: true });
        }
      }
    }
  }

  #updateLoadMoreProgress(loaded, total) {
    const { loadMoreProgressText, loadMoreProgressValue, loadMoreControl, loadMore, loadMoreCount } = this.refs;
    const template = loadMoreControl instanceof HTMLElement ? loadMoreControl.dataset.progressTemplate ?? '' : '';
    if (loadMoreProgressText) {
      loadMoreProgressText.textContent = this.#formatTemplate(template, { loaded, total });
    }
    if (loadMoreProgressValue instanceof HTMLElement) {
      const progress = total > 0 ? Math.min(100, Math.max(0, (loaded / total) * 100)) : 100;
      loadMoreProgressValue.style.setProperty('--collection-load-progress', `${progress}%`);
      if (loadMore instanceof HTMLElement) {
        loadMore.style.setProperty('--collection-load-progress', `${progress}%`);
      }
    }
    if (loadMoreCount instanceof HTMLElement) loadMoreCount.textContent = `(${loaded}/${total})`;
  }

  #formatTemplate(template, values) {
    return Object.entries(values).reduce(
      (copy, [key, value]) => copy.replaceAll(`__${key}__`, String(value)),
      template
    );
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    if (this.#isCollectionCatalog() && !this.#allowedCollectionViews().has(value)) return;

    if (this.#isCollectionCatalog()) {
      // Layout and controls change together; no idle wait, fetch, or temporary hidden cards.
      this.#setLayout(value);
      this.#syncCollectionControls(value);
      this.#armBackToTop();
      if (!Shopify.designMode) {
        try {
          sessionStorage.setItem(this.#isEditorialShopping() ? EDITORIAL_VIEW_STORAGE_KEY : COLLECTION_VIEW_STORAGE_KEY, value);
        } catch {}
        void this.#saveCollectionSnapshot();
      }
      return;
    }

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    this.#flushAllMediaRevealCohorts();
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);

    if (this.#isEditorialShopping()) {
      if (value === 'quick-buy') void loadEditorialQuickBuyRuntime();

      if (value !== 'editorial') {
        this.#editorialMediaObserver?.disconnect();
        this.#editorialMediaObserver = undefined;
        this.#editorialGridObserver?.disconnect();
        this.#editorialGridObserver = undefined;
        this.#editorialObservedGrid = undefined;
      }

      for (const card of grid.querySelectorAll('.tfp-editorial-buy')) {
        const primary = card.querySelector('img[data-editorial-primary]');
        if (!(primary instanceof HTMLImageElement)) continue;
        const isSingle = card.classList.contains('tfp-editorial-buy--single');
        const isTriptych = card.classList.contains('tfp-editorial-buy--triptych');
        primary.sizes = value === 'quick-buy' || (!isSingle && !isTriptych)
          ? '(min-width: 750px) 25vw, 50vw'
          : isTriptych ? '(min-width: 750px) 33vw, 66vw' : '(min-width: 750px) 50vw, 100vw';
      }
      if (value === 'editorial') this.#observeEditorialMedia();
      return;
    }

    if (!this.#usesClassicCollectionViews() || !COLLECTION_VIEWS.has(value)) return;

    for (const card of grid.querySelectorAll('product-card')) {
      const collectionCard = /** @type {HTMLElement & {syncCollectionView?: (view: 'standard'|'lookbook'|'single') => void}} */ (card);
      collectionCard.syncCollectionView?.(value);
    }

    for (const sizes of grid.querySelectorAll('product-card-sizes')) {
      sizes.toggleAttribute('data-auto-open', value === 'single' && !mediaQueryLarge.matches);
      sizes.syncCollectionView?.();
      if (sizes.hasAttribute('data-auto-open') && !customElements.get('product-card-sizes')) {
        sizes.dispatchEvent(new CustomEvent('tfp:card-sizes-auto-open', { bubbles: true }));
      }
    }
  }

  #observeEditorialMedia() {
    const { grid } = this.refs;
    if (!(grid instanceof HTMLElement)) return;
    if (!('IntersectionObserver' in window)) {
      for (const template of grid.querySelectorAll('template[data-editorial-extra]')) {
        const media = template.closest('.tfp-editorial-buy__media');
        if (!(template instanceof HTMLTemplateElement) || !(media instanceof HTMLElement)) continue;
        media.append(template.content.cloneNode(true));
        template.remove();
      }
      return;
    }

    const editorialBudget = getSpeculativeLoadingBudget();
    const editorialLookahead = editorialBudget === 'none' ? 0 : editorialBudget === 'conservative' ? 300 : 600;

    this.#editorialMediaObserver ??= new IntersectionObserver((entries) => {
      if (!this.isConnected || this.refs.grid?.getAttribute('product-grid-view') !== 'editorial') return;
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        this.#editorialMediaObserver?.unobserve(entry.target);
        const template = entry.target.querySelector('template[data-editorial-extra]');
        const media = entry.target.querySelector('.tfp-editorial-buy__media');
        if (!(template instanceof HTMLTemplateElement) || !(media instanceof HTMLElement)) continue;
        media.append(template.content.cloneNode(true));
        template.remove();
      }
    }, { rootMargin: `${editorialLookahead}px` });

    const observeCards = () => {
      if (grid.getAttribute('product-grid-view') !== 'editorial') return;
      for (const template of grid.querySelectorAll('template[data-editorial-extra]')) {
        const card = template.closest('.tfp-editorial-buy');
        if (card) this.#editorialMediaObserver?.observe(card);
      }
    };
    observeCards();
    if (this.#editorialObservedGrid !== grid) {
      this.#editorialGridObserver?.disconnect();
      this.#editorialGridObserver = new MutationObserver(observeCards);
      this.#editorialGridObserver.observe(grid, { childList: true, subtree: true });
      this.#editorialObservedGrid = grid;
    }
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    if (this.#isCollectionCatalog()) {
      this.#restoreCollectionView();
      return;
    }

    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };

  /**
   * Whether this results list owns the Collection Standard/Lookbook contract.
   *
   * @returns {boolean}
   */
  #isCollectionCatalog() {
    return this.dataset.catalogViewContract === 'standard-lookbook' ||
      this.dataset.catalogViewContract === 'editorial-shopping';
  }

  #isSearchResults() {
    return this.dataset.pageType === 'search';
  }

  #usesClassicCollectionViews() {
    return this.dataset.catalogViewContract === 'standard-lookbook';
  }

  #isEditorialShopping() {
    return this.dataset.catalogViewContract === 'editorial-shopping';
  }

  #allowedCollectionViews() {
    return this.#isEditorialShopping() ? EDITORIAL_VIEWS : this.#isSearchResults() ? SEARCH_VIEWS : COLLECTION_VIEWS;
  }

  #collectionSignature(url = new URL(window.location.href)) {
    const signature = new URL(url);
    signature.hash = '';
    signature.searchParams.delete('page');
    signature.searchParams.sort();
    return `${signature.pathname}?${signature.searchParams.toString()}`;
  }

  #collectionSnapshotContext() {
    return JSON.stringify([this.sectionId, this.#collectionSignature(), this.dataset.historyContext,
      this.refs.grid?.getAttribute('product-grid-view'), this.dataset.historyPageSize]);
  }

  #saveCollectionSnapshot() {
    const state = history.state ?? {};
    if (Shopify.designMode || this.paginationMode !== 'adaptive' || this.#adaptiveHistoryRestorePending ||
        this.#renderedCollectionSignature !== this.#collectionSignature() || !state.tfpCollectionEntryId) return;
    return this.saveAdaptiveHistorySnapshot({ entryId: state.tfpCollectionEntryId,
      context: this.#collectionSnapshotContext(), startPage: Number(state.tfpCollectionStartPage),
      endPage: Number(state.tfpCollectionPage), maxPages: MAX_ADAPTIVE_APPENDS + 1 });
  }

  /**
   * Save only entry-scoped geometry on departure; HTML persistence is prepared after Load More.
   * @param {Element | null} [preferredAnchor]
   */
  captureCollectionHistory = (preferredAnchor) => {
    if (Shopify.designMode || this.paginationMode !== 'adaptive' || this.#adaptiveHistoryRestorePending ||
        this.#collectionPopRenderPending || this.#renderedCollectionSignature !== this.#collectionSignature()) return;
    const { grid } = this.refs;
    if (!(grid instanceof HTMLElement)) return;
    const cards = Array.from(grid.children).filter((card) => card instanceof HTMLElement && card.classList.contains('product-grid__item'));
    if (preferredAnchor instanceof HTMLElement && preferredAnchor.parentElement === grid &&
        preferredAnchor.classList.contains('product-grid__item') && preferredAnchor.id) {
      this.#departureAnchorId = preferredAnchor.id;
    }
    const departureAnchor = this.#departureAnchorId ? document.getElementById(this.#departureAnchorId) : null;
    const anchor = departureAnchor instanceof HTMLElement && departureAnchor.parentElement === grid &&
      departureAnchor.classList.contains('product-grid__item') ? departureAnchor : cards.find((card) => {
      const bounds = card.getBoundingClientRect();
      return bounds.bottom > 0 && bounds.top < window.innerHeight;
    });
    const state = history.state ?? {};
    try {
      history.replaceState({ ...state, scrollTop: getScrollTop(), tfpCollectionPosition: {
        anchorId: anchor?.id, anchorTop: anchor?.getBoundingClientRect().top,
        width: window.innerWidth, height: grid.getBoundingClientRect().height,
        view: grid.getAttribute('product-grid-view'), context: this.#collectionSnapshotContext(),
      } }, '');
    } catch {
      return;
    }
  };

  #clearHistoryGate() {
    window.clearTimeout(this.historyGateTimer);
    this.removeAttribute('data-history-restoring');
    this.removeAttribute('data-history-positioning');
    this.style.removeProperty('--collection-history-height');
    if (this.refs.loadMoreStatus) this.refs.loadMoreStatus.textContent = '';
  }

  /** A bounded restore failure leaves the URL's server page usable and cancels late insertions. */
  cancelCollectionHistoryRestore() {
    const restorePosition = this.#historyRestoreIntent === this.#collectionScrollIntent;
    const state = history.state ?? {};
    const position = state.tfpCollectionPosition;
    const scrollTop = Number(state.scrollTop);
    const targetId = this.#getCollectionTargetId(position);
    this.#adaptiveHistoryRestoreGeneration += 1;
    this.#cancelCollectionPositionRestore();
    this.#adaptiveHistoryRestorePending = false;
    this.#adaptiveHistoryRestoreKey = '';
    this.#adaptiveHistoryRestoreTask = undefined;
    window.clearTimeout(this.#historyRestoreTimer);
    this.resetPaginationState();
    this.#clearHistoryGate();
    const page = Number(new URL(window.location.href).searchParams.get('page')) || 1;
    history.replaceState({ ...state, tfpCollectionStartPage: page, tfpCollectionPage: page,
      tfpCollectionPosition: undefined, scrollTop: undefined }, '');
    this.#successfulAdaptiveAppends = 0;
    this.#loadMoreFailureFallback = false;
    this.removeAttribute('data-adaptive-pagination-locked');
    this.#setCollectionNavigationState('error');
    if (restorePosition) this.#restoreCollectionPosition(targetId, scrollTop, position);
  }

  #syncCollectionDocumentTitle() {
    const template = this.dataset.collectionTitleTemplate;
    const pageLabel = this.dataset.collectionTitlePageLabel;
    if (!template || !pageLabel || !this.isConnected || Shopify.designMode) return;
    const url = new URL(window.location.href);
    if (url.pathname !== this.dataset.collectionTitlePath) return;
    const requestedPage = Number(url.searchParams.get('page'));
    const page = Number.isInteger(requestedPage) && requestedPage > 1 ? requestedPage : 1;
    const suffix = page > 1 ? ` – ${pageLabel.replace('__TFP_PAGE_NUMBER__', String(page))}` : '';
    const title = template.replace('__TFP_COLLECTION_PAGE__', suffix);
    if (document.title !== title) document.title = title;
  }

  #setCollectionNavigationState(state, generation = this.#collectionPopRenderGeneration) {
    this.dataset.collectionNavigationState = state;
    this.dataset.collectionNavigationGeneration = String(generation);
    const isPending = state === 'pending';
    if (isPending) {
      this.setAttribute('aria-busy', 'true');
      this.refs.grid?.setAttribute('aria-busy', 'true');
    } else {
      this.removeAttribute('aria-busy');
      this.refs.grid?.removeAttribute('aria-busy');
    }
    this.#observeLoadMorePrefetch();
    if (state === 'ready') this.#syncCollectionDocumentTitle();
  }

  #getCollectionTargetId(position = history.state?.tfpCollectionPosition) {
    try {
      return decodeURIComponent(window.location.hash.slice(1)) || position?.anchorId || '';
    } catch {
      return window.location.hash.slice(1) || position?.anchorId || '';
    }
  }

  #cancelCollectionPositionRestore() {
    this.#positionRestoreGeneration += 1;
    if (this.#positionRestoreFrame !== undefined) cancelAnimationFrame(this.#positionRestoreFrame);
    this.#positionRestoreFrame = undefined;
    const resolve = this.#positionRestoreResolve;
    this.#positionRestoreResolve = undefined;
    resolve?.(false);
  }

  #restoreCollectionPosition(targetId, scrollTop, position) {
    this.#cancelCollectionPositionRestore();
    const anchor = position?.anchorId ? document.getElementById(position.anchorId) : null;
    if (anchor && Number.isFinite(position.anchorTop) && position.width === window.innerWidth &&
        position.view === this.refs.grid?.getAttribute('product-grid-view')) {
      const positionRestoreGeneration = this.#positionRestoreGeneration;
      const popRenderGeneration = this.#collectionPopRenderGeneration;
      const adaptiveRestoreGeneration = this.#adaptiveHistoryRestoreGeneration;
      const scrollIntent = this.#collectionScrollIntent;
      const startedAt = performance.now();
      let frameCount = 0;
      let stableFrames = 0;

      return new Promise((resolve) => {
        this.#positionRestoreResolve = resolve;
        const finish = (aligned) => {
          if (positionRestoreGeneration === this.#positionRestoreGeneration) this.#positionRestoreResolve = undefined;
          resolve(aligned);
        };
        const alignAnchor = () => {
          this.#positionRestoreFrame = undefined;
          if (!this.isConnected || !anchor.isConnected || positionRestoreGeneration !== this.#positionRestoreGeneration ||
              popRenderGeneration !== this.#collectionPopRenderGeneration ||
              adaptiveRestoreGeneration !== this.#adaptiveHistoryRestoreGeneration ||
              scrollIntent !== this.#collectionScrollIntent) { finish(false); return; }

          frameCount += 1;
          const error = anchor.getBoundingClientRect().top - position.anchorTop;
          if (!Number.isFinite(error)) { finish(false); return; }
          if (Math.abs(error) <= HISTORY_POSITION_EPSILON) {
            stableFrames += 1;
          } else {
            const before = getScrollTop();
            scrollTo({ top: before + error, behavior: 'instant' });
            stableFrames = 0;
            // A temporarily short restored document can clamp this write. Retry
            // within the same deadline while deferred card geometry settles.
          }

          if (stableFrames >= HISTORY_POSITION_SETTLE_STABLE_FRAMES ||
              frameCount >= HISTORY_POSITION_SETTLE_MAX_FRAMES ||
              performance.now() - startedAt >= HISTORY_POSITION_SETTLE_MS) {
            finish(stableFrames >= HISTORY_POSITION_SETTLE_STABLE_FRAMES);
            return;
          }
          this.#positionRestoreFrame = requestAnimationFrame(alignAnchor);
        };

        alignAnchor();
      });
    }
    if (anchor) {
      anchor.scrollIntoView({ behavior: 'instant', block: 'start' });
      return;
    }
    // Entry geometry survives header/font/breakpoint changes better than a raw
    // number. The shared scroll owner supplies the numeric fallback.
    if (Number.isFinite(scrollTop) && scrollTop >= 0) {
      scrollTo({ top: scrollTop, behavior: 'instant' });
      return;
    }

    const targetCard = targetId ? document.getElementById(targetId) : null;
    targetCard?.scrollIntoView({ behavior: 'instant', block: 'start' });
  }

  #handleCollectionPageShow = (event) => {
    cancelAnimationFrame(this.#collectionPageShowFrame);
    this.#cancelCollectionPositionRestore();
    this.#departureAnchorId = '';
    this.#persistedCollectionUrl = event.persisted ? window.location.href : '';
    if (event.persisted) {
      if (!this.#adaptiveHistoryRestorePending) {
        this.#clearHistoryGate();
        this.#setCollectionNavigationState('ready');
      }
    }
    this.#collectionPageShowFrame = requestAnimationFrame(() => {
      this.#collectionPageShowFrame = undefined;
      this.#persistedCollectionUrl = '';
      if (!event.persisted && this.isConnected) this.#restoreAdaptiveHistory(history.state ?? {}).catch(() => {});
    });
  };

  async #restoreAdaptiveHistory(state, scrollIntent = this.#collectionScrollIntent) {
    if (Shopify.designMode || !this.#isCollectionCatalog()) return false;
    if (this.paginationMode !== 'adaptive') {
      const gated = this.hasAttribute('data-history-restoring');
      this.#clearHistoryGate();
      if (gated) {
        const position = state.tfpCollectionPosition;
        const scrollTop = Number(state.scrollTop);
        const targetId = this.#getCollectionTargetId(position);
        const page = Number(new URL(window.location.href).searchParams.get('page')) || 1;
        history.replaceState({ ...state, tfpCollectionStartPage: page, tfpCollectionPage: page,
          tfpCollectionPosition: undefined, scrollTop: undefined }, '');
        syncScrollRestorationMode();
        this.#restoreCollectionPosition(targetId, scrollTop, position);
      }
      return false;
    }

    const startPage = Number(state.tfpCollectionStartPage) || 1;
    const endPage = Number(state.tfpCollectionPage);
    const targetId = this.#getCollectionTargetId(state.tfpCollectionPosition);
    if (
      !Number.isInteger(startPage) ||
      !Number.isInteger(endPage) ||
      startPage < 1 ||
      endPage <= startPage ||
      endPage - startPage > MAX_ADAPTIVE_APPENDS
    )
      { this.#clearHistoryGate(); return false; }

    const restoreKey = `${window.location.href}|${state.tfpCollectionEntryId ?? ''}|${startPage}:${endPage}`;
    if (this.#adaptiveHistoryRestoreKey === restoreKey && this.#adaptiveHistoryRestoreTask) return this.#adaptiveHistoryRestoreTask;
    const cards = Array.from(this.refs.grid?.children ?? []).filter((card) =>
      card instanceof HTMLElement && card.classList.contains('product-grid__item'));
    const pages = new Set(cards.map((card) => Number(card.getAttribute('data-page'))));
    const complete = pages.size === endPage - startPage + 1 &&
      Array.from({ length: endPage - startPage + 1 }, (_, index) => startPage + index).every((page) => pages.has(page));
    if (complete && this.#renderedCollectionSignature === this.#collectionSignature() && !this.#collectionPopRenderPending) {
      this.#clearHistoryGate();
      if (scrollIntent === this.#collectionScrollIntent) this.#restoreCollectionPosition(targetId, Number(state.scrollTop), state.tfpCollectionPosition);
      this.#setCollectionNavigationState('ready');
      return true;
    }
    this.#adaptiveHistoryRestoreKey = restoreKey;
    const task = this.#runAdaptiveHistoryRestore(state, startPage, endPage, targetId, scrollIntent);
    this.#adaptiveHistoryRestoreTask = task;
    void task.finally(() => requestAnimationFrame(() => {
      if (this.#adaptiveHistoryRestoreTask === task) {
        this.#adaptiveHistoryRestoreKey = '';
        this.#adaptiveHistoryRestoreTask = undefined;
      }
    }));
    return task;
  }

  async #runAdaptiveHistoryRestore(state, startPage, endPage, targetId, scrollIntent) {
    this.#cancelCollectionPositionRestore();
    const restoreGeneration = ++this.#adaptiveHistoryRestoreGeneration;
    this.#adaptiveHistoryRestorePending = true;
    this.#historyRestoreIntent = scrollIntent;
    this.setAttribute('data-history-restoring', '');
    const position = state.tfpCollectionPosition;
    if (position?.width === window.innerWidth && position.height > 0 && position.height < 500000) {
      this.style.setProperty('--collection-history-height', `${position.height}px`);
    }
    this.#setCollectionNavigationState('pending', restoreGeneration);
    if (this.refs.loadMoreStatus) this.refs.loadMoreStatus.textContent = this.dataset.loadMoreLoadingLabel ?? '';
    window.clearTimeout(this.#historyRestoreTimer);
    this.#historyRestoreTimer = window.setTimeout(() => this.cancelCollectionHistoryRestore(), HISTORY_RESTORE_TIMEOUT);

    try {
      const result = await this.restoreAdaptiveRange({
        startPage,
        endPage,
        maxPages: MAX_ADAPTIVE_APPENDS + 1,
        // The partial URL page is hidden during a cold restore, so it cannot be a visual anchor.
        preserveScroll: () => !this.hasAttribute('data-history-restoring') && scrollIntent !== this.#collectionScrollIntent,
        snapshot: { entryId: state.tfpCollectionEntryId, context: this.#collectionSnapshotContext() },
      });
      if (restoreGeneration !== this.#adaptiveHistoryRestoreGeneration) return true;
      if (!result) throw new Error('Unable to restore adaptive collection history');

      if (result.endPage !== endPage) {
        const repairedUrl = new URL(window.location.href);
        const currentPage = Number(repairedUrl.searchParams.get('page'));
        if (Number.isInteger(currentPage) && currentPage > result.endPage) {
          repairedUrl.searchParams.set('page', String(result.endPage));
        }
        history.replaceState(
          { ...state, tfpCollectionPage: result.endPage },
          '',
          repairedUrl.toString()
        );
      }

      this.#successfulAdaptiveAppends = result.appendCount;
      if (result.appendCount > 0) this.dataset.adaptivePaginationLocked = 'true';

      const total = Number(this.refs.loadMoreControl?.dataset.totalCount) || result.loadedCount;
      this.#applyLoadMorePresentation({ nextUrl: result.nextUrl, loaded: result.loadedCount, total });
      this.#restoreCollectionView();
      this.#renderedCollectionSignature = this.#collectionSignature();

      await new Promise((resolve) => requestAnimationFrame(resolve));
      if (restoreGeneration !== this.#adaptiveHistoryRestoreGeneration) return true;
      // Keep the complete range invisible, but laid out, until the clicked
      // card has its saved viewport position. Reveal only after alignment.
      this.setAttribute('data-history-positioning', '');
      this.removeAttribute('data-history-restoring');
      this.style.removeProperty('--collection-history-height');
      if (scrollIntent === this.#collectionScrollIntent) {
        await this.#restoreCollectionPosition(targetId, Number(state.scrollTop), position);
      }
      if (restoreGeneration !== this.#adaptiveHistoryRestoreGeneration) return true;
      this.#clearHistoryGate();
      this.#adaptiveHistoryRestorePending = false;
      this.dataset.collectionHistorySource = result.source ?? 'network';
      this.#setCollectionNavigationState('ready', restoreGeneration);
      if (result.source !== 'snapshot') void this.#saveCollectionSnapshot();
      return true;
    } catch {
      if (restoreGeneration !== this.#adaptiveHistoryRestoreGeneration) return true;
      this.cancelCollectionHistoryRestore();
      return true;
    } finally {
      if (restoreGeneration === this.#adaptiveHistoryRestoreGeneration) window.clearTimeout(this.#historyRestoreTimer);
    }
  }

  /**
   * Restores the collection view after initial render or a section morph.
   * @param {boolean} [initialRestore] - Whether the untouched server-rendered view can be retained.
   */
  #restoreCollectionView(initialRestore = false) {
    if (!this.#isCollectionCatalog()) return;

    const { grid } = this.refs;
    if (!(grid instanceof HTMLElement)) return;

    const allowedViews = this.#allowedCollectionViews();
    const defaultView = allowedViews.has(grid.dataset.catalogViewDefault)
      ? grid.dataset.catalogViewDefault
      : this.#isEditorialShopping() ? 'editorial' : 'standard';
    let view = defaultView;

    if (!Shopify.designMode) {
      try {
        const storedView = sessionStorage.getItem(this.#isEditorialShopping() ? EDITORIAL_VIEW_STORAGE_KEY : COLLECTION_VIEW_STORAGE_KEY);
        if (storedView && allowedViews.has(storedView)) view = storedView;
      } catch {
        view = defaultView;
      }
    }

    if (view === 'single' && mediaQueryLarge.matches) view = 'standard';

    if (
      !initialRestore ||
      Shopify.designMode ||
      view !== 'standard' ||
      grid.getAttribute('product-grid-view') !== view
    ) {
      this.#setLayout(view);
    }
    this.#syncCollectionControls(view);
    this.#armBackToTop();
    this.#observeLoadMorePrefetch();
  }

  beginFilterNavigation() {
    if (!this.#isCollectionCatalog()) return;

    const isSearch = this.#isSearchResults();
    const navigationGeneration = isSearch
      ? ++this.#searchPopRenderGeneration
      : ++this.#collectionPopRenderGeneration;
    if (!isSearch) {
      this.#collectionPopRenderPending = false;
      this.#resetAdaptiveState();
    }
    this.#setCollectionNavigationState('pending', navigationGeneration);
    return navigationGeneration;
  }

  finishFilterNavigation(navigationGeneration, state = 'error') {
    const currentGeneration = this.#isSearchResults()
      ? this.#searchPopRenderGeneration
      : this.#collectionPopRenderGeneration;
    if (!this.isConnected || navigationGeneration !== currentGeneration) return;

    this.#setLoadMoreBusy(false);
    if (this.refs.loadMoreLabel) this.refs.loadMoreLabel.textContent = this.dataset.loadMoreIdleLabel ?? '';
    this.#setCollectionNavigationState(state, navigationGeneration);
  }

  beginCollectionFilterNavigation() {
    if (this.#isSearchResults()) return;
    return this.beginFilterNavigation();
  }

  finishCollectionFilterNavigation(navigationGeneration, state = 'error') {
    if (this.#isSearchResults()) return;
    this.finishFilterNavigation(navigationGeneration, state);
  }

  /**
   * Reapplies the collection view after filter, sort, or clear-all section rendering.
   *
   * @param {Event & { promise?: Promise<unknown> }} event
   */
  #handleCollectionUpdate = (event) => {
    if (!this.#isCollectionCatalog()) return;

    const eventSection = event.target instanceof Element ? event.target.closest('.shopify-section') : null;
    const ownSection = this.closest('.shopify-section');
    if (eventSection && ownSection && eventSection !== ownSection) return;

    const navigationGeneration = this.beginCollectionFilterNavigation();
    if (navigationGeneration == null) return;

    if (!event.promise) {
      this.#setCollectionNavigationState('ready', navigationGeneration);
      return;
    }

    event.promise
      .then(() =>
        requestAnimationFrame(() => {
          if (!this.isConnected || navigationGeneration !== this.#collectionPopRenderGeneration) return;
          this.#renderedCollectionSignature = this.#collectionSignature();
          this.#restoreCollectionView();
          this.#setCollectionNavigationState('ready', navigationGeneration);
        })
      )
      .catch(() => {
        this.finishCollectionFilterNavigation(navigationGeneration);
      });
  };

  #handleSearchPopState = () => {
    if (!this.#isSearchResults()) return;

    const renderGeneration = this.beginFilterNavigation();
    if (renderGeneration == null) return;

    import('@theme/section-renderer')
      .then(({ sectionRenderer }) => {
        if (!this.isConnected || renderGeneration !== this.#searchPopRenderGeneration) return;
        return sectionRenderer.renderSection(this.sectionId, { cache: false, url: new URL(window.location.href) });
      })
      .then(() => {
        if (!this.isConnected || renderGeneration !== this.#searchPopRenderGeneration) return;
        this.#renderedCollectionSignature = this.#collectionSignature();
        this.#restoreCollectionView();
        this.finishFilterNavigation(renderGeneration, 'ready');
      })
      .catch(() => {
        if (!this.isConnected || renderGeneration !== this.#searchPopRenderGeneration) return;
        this.finishFilterNavigation(renderGeneration);
        window.location.reload();
      });
  };

  #handleCollectionPopState = (event) => {
    this.#syncCollectionDocumentTitle();
    if (!this.#isCollectionCatalog() || !event.state?.tfpCollectionHistory) return;

    const persistedReturn = this.#persistedCollectionUrl === window.location.href;
    cancelAnimationFrame(this.#collectionPageShowFrame);
    this.#collectionPageShowFrame = undefined;
    this.#persistedCollectionUrl = '';
    if (persistedReturn) return;

    if (this.#adaptiveHistoryRestoreKey.startsWith(`${window.location.href}|`) && this.#adaptiveHistoryRestoreTask) return;

    this.#cancelCollectionPositionRestore();
    const renderGeneration = ++this.#collectionPopRenderGeneration;
    const scrollIntent = this.#collectionScrollIntent;
    const scrollTop = Number(event.state.scrollTop);
    const targetId = this.#getCollectionTargetId(event.state.tfpCollectionPosition);

    const urlPage = Number(new URL(window.location.href).searchParams.get('page')) || 1;
    const startPage = Number(event.state.tfpCollectionStartPage) || urlPage;
    const endPage = Number(event.state.tfpCollectionPage) || urlPage;
    const renderedPages = new Set(Array.from(this.refs.grid?.children ?? [])
      .filter((card) => card instanceof HTMLElement && card.classList.contains('product-grid__item'))
      .map((card) => Number(card.getAttribute('data-page'))));
    const validRange = Number.isInteger(startPage) && Number.isInteger(endPage) && startPage >= 1 &&
      endPage >= startPage && endPage - startPage <= MAX_ADAPTIVE_APPENDS;
    const completeRange = validRange && renderedPages.size === endPage - startPage + 1 &&
      Array.from({ length: endPage - startPage + 1 }, (_, index) => startPage + index).every((page) => renderedPages.has(page));
    if (
      !this.#adaptiveHistoryRestorePending &&
      !this.#collectionPopRenderPending &&
      this.#renderedCollectionSignature === this.#collectionSignature()
    ) {
      if (completeRange) {
        requestAnimationFrame(() => {
          if (!this.isConnected || renderGeneration !== this.#collectionPopRenderGeneration) return;
          if (scrollIntent === this.#collectionScrollIntent && (endPage > startPage || event.state.scrollTop == null)) {
            this.#restoreCollectionPosition(targetId, scrollTop, event.state.tfpCollectionPosition);
          }
          this.#setCollectionNavigationState('ready', renderGeneration);
        });
        return;
      }
      if (validRange && endPage > startPage && this.paginationMode === 'adaptive') {
        this.#restoreAdaptiveHistory(event.state).catch(() => {});
        return;
      }
    }

    this.#collectionPopRenderPending = true;
    this.#setCollectionNavigationState('pending', renderGeneration);
    this.#resetAdaptiveState();
    if (validRange && endPage > startPage && this.paginationMode === 'adaptive') {
      this.setAttribute('data-history-restoring', '');
    }
    import('@theme/section-renderer')
      .then(({ sectionRenderer }) => {
        if (!this.isConnected || renderGeneration !== this.#collectionPopRenderGeneration) return;
        // Back/Forward targets a URL the shopper already rendered. Reuse the
        // existing SectionRenderer cache when present; cache misses still fetch.
        return sectionRenderer.renderSection(this.sectionId, { cache: true, url: new URL(window.location.href) });
      })
      .then(async () => {
        if (!this.isConnected || renderGeneration !== this.#collectionPopRenderGeneration) return;
        this.#collectionPopRenderPending = false;
        this.#renderedCollectionSignature = this.#collectionSignature();

        const filtersPanel = document.querySelector('#filters-panel');
        if (
          filtersPanel instanceof HTMLElement &&
          filtersPanel.isOpen &&
          typeof filtersPanel.refreshLazyFragment === 'function'
        ) {
          // Popstate renders the collection section directly, bypassing FacetsForm's
          // normal panel refresh. Keep the open panel's option state in step with
          // the URL, without allowing a fragment failure to disrupt history restore.
          await filtersPanel.refreshLazyFragment().catch(() => {});
          if (!this.isConnected || renderGeneration !== this.#collectionPopRenderGeneration) return;
        }

        const restoredRange = await this.#restoreAdaptiveHistory(event.state, scrollIntent);
        if (!this.isConnected || renderGeneration !== this.#collectionPopRenderGeneration || restoredRange) return;

        requestAnimationFrame(() => {
          if (!this.isConnected || renderGeneration !== this.#collectionPopRenderGeneration) return;
          this.#restoreCollectionView();
          if (scrollIntent === this.#collectionScrollIntent) this.#restoreCollectionPosition(targetId, scrollTop, event.state.tfpCollectionPosition);
          this.#setCollectionNavigationState('ready', renderGeneration);
        });
      })
      .catch(() => {
        if (this.isConnected && renderGeneration === this.#collectionPopRenderGeneration) {
          this.#collectionPopRenderPending = false;
          this.#setCollectionNavigationState('error', renderGeneration);
          window.location.reload();
        }
      });
  };

  #resetAdaptiveState() {
    this.#adaptiveHistoryRestoreGeneration += 1;
    this.#cancelCollectionPositionRestore();
    delete this.dataset.collectionHistorySource;
    this.#adaptiveHistoryRestorePending = false;
    this.#adaptiveHistoryRestoreKey = '';
    this.#adaptiveHistoryRestoreTask = undefined;
    window.clearTimeout(this.#historyRestoreTimer);
    this.#clearHistoryGate();
    this.resetPaginationState();
    this.#successfulAdaptiveAppends = 0;
    this.#loadMoreFailureFallback = false;
    this.removeAttribute('data-adaptive-pagination-locked');
  }

  /**
   * Keeps both mobile and desktop collection controls in sync.
   *
   * @param {string} value
   */
  #syncCollectionControls(value) {
    for (const option of this.querySelectorAll('input[name="tfp-collection-view"]')) {
      if (option instanceof HTMLInputElement) option.checked = option.value === value;
    }
  }
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}



